import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    wishlistItems: [],
    loading: false,
    error: null,
    pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 12
    }
};

const wishlistSlice = createSlice({
    name: "wishlist",
    initialState,
    reducers: {
        // Set loading state
        setLoading: (state, action) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },

        // Set error state
        setError: (state, action) => {
            state.error = action.payload;
            state.loading = false;
        },

        // Initialize wishlist with API data
        initializeWishlist: (state, action) => {
            state.wishlistItems = Array.isArray(action.payload) ? action.payload : [];
            state.loading = false;
            state.error = null;
        },

        // Set wishlist items (for API responses)
        setWishlistItems: (state, action) => {
            const { wishlist = [], pagination = {} } = action.payload || {};
            state.wishlistItems = Array.isArray(wishlist) ? wishlist : [];
            state.pagination = { ...state.pagination, ...pagination };
            state.loading = false;
            state.error = null;
        },

        // Toggle wishlist item (handles both old and new schemas)
ToggleWishlist: (state, action) => {
    const product = action.payload;
    if (!product) return;

    // Get the product ID and variant info
    const productId = product.product?._id || product._id;
    const variantSku = product.variantSku || product.selectedVariant?.sku;

    if (!Array.isArray(state.wishlistItems)) {
        state.wishlistItems = [product];
        return;
    }

    // Find existing item (considering variants)
    const existingIndex = state.wishlistItems.findIndex(item => {
        const itemProductId = item.product?._id || item._id;
        const itemVariantSku = item.variantSku || item.selectedVariant?.sku;
        
        // If both have variants, match both product and variant
        if (variantSku && itemVariantSku) {
            return itemProductId === productId && itemVariantSku === variantSku;
        }
        // If neither has variant, match just product
        if (!variantSku && !itemVariantSku) {
            return itemProductId === productId;
        }
        // If only one has variant, no match
        return false;
    });

    if (existingIndex !== -1) {
        // Remove from wishlist
        state.wishlistItems.splice(existingIndex, 1);
        state.pagination.totalItems = Math.max(0, state.pagination.totalItems - 1);
    } else {
        // Add to wishlist
        state.wishlistItems.push(product);
        state.pagination.totalItems += 1;
    }
},

        // SEPARATE ACTION FOR DIRECT REMOVAL (for delete buttons)
        removeFromWishlistById: (state, action) => {
            const { wishlistItemId } = action.payload;
            if (!Array.isArray(state.wishlistItems) || !wishlistItemId) return;

            const existingIndex = state.wishlistItems.findIndex(item => 
                item._id?.toString() === wishlistItemId.toString()
            );

            if (existingIndex !== -1) {
                state.wishlistItems.splice(existingIndex, 1);
                state.pagination.totalItems = Math.max(0, state.pagination.totalItems - 1);
            }
        },

        // Remove specific item from wishlist
        removeFromWishlist: (state, action) => {
            const { productId, variantSku, itemId } = action.payload || {};
            
            if (!Array.isArray(state.wishlistItems)) {
                return;
            }

            let indexToRemove = -1;

            if (itemId) {
                // Remove by wishlist item ID
                indexToRemove = state.wishlistItems.findIndex(item => 
                    item._id === itemId
                );
            } else if (productId) {
                // Remove by product ID and optional variant
                indexToRemove = state.wishlistItems.findIndex(item => {
                    const itemProductId = item.product?._id || item._id;
                    const itemVariantSku = item.selectedVariant?.sku || item.variantSku;
                    
                    if (variantSku && itemVariantSku) {
                        return itemProductId === productId && itemVariantSku === variantSku;
                    }
                    if (!variantSku && !itemVariantSku) {
                        return itemProductId === productId;
                    }
                    return false;
                });
            }

            if (indexToRemove !== -1) {
                state.wishlistItems.splice(indexToRemove, 1);
                state.pagination.totalItems = Math.max(0, state.pagination.totalItems - 1);
            }
        },

        // Add item to wishlist
        addToWishlist: (state, action) => {
            const product = action.payload;
            if (!product) return;

            if (!Array.isArray(state.wishlistItems)) {
                state.wishlistItems = [];
            }

            const productId = product.product?._id || product._id;
            const variantSku = product.selectedVariant?.sku || product.variantSku;

            // Check if item already exists
            const exists = state.wishlistItems.some(item => {
                const itemProductId = item.product?._id || item._id;
                const itemVariantSku = item.selectedVariant?.sku || item.variantSku;
                
                if (variantSku && itemVariantSku) {
                    return itemProductId === productId && itemVariantSku === variantSku;
                }
                if (!variantSku && !itemVariantSku) {
                    return itemProductId === productId;
                }
                return false;
            });

            if (!exists) {
                state.wishlistItems.push(product);
                state.pagination.totalItems += 1;
            }
        },

        // Clear entire wishlist
        clearWishlist: (state) => {
            state.wishlistItems = [];
            state.pagination = {
                ...initialState.pagination
            };
            state.error = null;
        },

        // Update pagination info
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },

        // Check if product is in wishlist (utility)
        checkWishlistStatus: (state, action) => {
            const { productId, variantSku } = action.payload || {};
            
            if (!Array.isArray(state.wishlistItems) || !productId) {
                return false;
            }

            return state.wishlistItems.some(item => {
                const itemProductId = item.product?._id || item._id;
                const itemVariantSku = item.selectedVariant?.sku || item.variantSku;
                
                if (variantSku && itemVariantSku) {
                    return itemProductId === productId && itemVariantSku === variantSku;
                }
                if (!variantSku && !itemVariantSku) {
                    return itemProductId === productId;
                }
                return false;
            });
        }
    }
});

// Selectors
export const selectWishlistItems = (state) => state.wishlist.wishlistItems || [];
export const selectWishlistLoading = (state) => state.wishlist.loading;
export const selectWishlistError = (state) => state.wishlist.error;
export const selectWishlistPagination = (state) => state.wishlist.pagination;
export const selectWishlistCount = (state) => {
    const items = state.wishlist.wishlistItems;
    return Array.isArray(items) ? items.length : 0;
};

// Check if specific product/variant is in wishlist
export const selectIsInWishlist = (productId, variantSku = null) => (state) => {
    const items = state.wishlist.wishlistItems;
    if (!Array.isArray(items) || !productId) return false;
    
    return items.some(item => {
        const itemProductId = item.product?._id || item._id;
        const itemVariantSku = item.selectedVariant?.sku || item.variantSku;
        
        if (variantSku && itemVariantSku) {
            return itemProductId === productId && itemVariantSku === variantSku;
        }
        if (!variantSku && !itemVariantSku) {
            return itemProductId === productId;
        }
        return false;
    });
};

export const {
    setLoading,
    setError,
    initializeWishlist,
    setWishlistItems,
    ToggleWishlist,
    removeFromWishlist,
    removeFromWishlistById,
    addToWishlist,
    clearWishlist,
    setPagination,
    checkWishlistStatus
} = wishlistSlice.actions;

export default wishlistSlice.reducer;